import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Download, ArrowLeft } from 'lucide-react';
import { generatePDF, storeProposal } from '../lib/api';
import { toast } from './ui/use-toast';

export default function ProposalResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const proposalRef = useRef<HTMLDivElement>(null);
  const proposal = location.state?.proposal;
  const formData = location.state?.formData;

  useEffect(() => {
    if (!proposal) {
      navigate('/');
    }
  }, [proposal, navigate]);

  const handleDownloadPDF = async () => {
    if (!proposalRef.current || !formData) return;

    try {
      const pdfUrl = await generatePDF(proposalRef.current);
      await storeProposal(formData, proposal, pdfUrl);

      toast({
        title: "PDF Generated",
        description: "The proposal has been converted to PDF and stored successfully.",
      });

      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!proposal) return null;

  const sections = proposal.split('\n\n').reduce((acc: string[], section: string) => {
    if (section.trim()) {
      acc.push(section.trim());
    }
    return acc;
  }, []);

  const formatText = (text: string) => {
    return text
      .replace(/\*\*/g, '')
      .replace(/[-|]/g, '');
  };

  // Get today's date and add 30 days
  const today = new Date();
  const validUntil = new Date(today);
  validUntil.setDate(today.getDate() + 30);

  // Format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8 flex items-center justify-between">
        <Button 
          onClick={() => navigate('/')} 
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Form
        </Button>
        <Button 
          onClick={handleDownloadPDF}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Download PDF
        </Button>
      </div>

      <div 
        ref={proposalRef}
        className="bg-card rounded-lg shadow-lg p-8"
      >
        <h2 className="text-3xl font-bold tracking-tight mb-6">Project Proposal</h2>
        
        <div className="prose prose-slate max-w-none">
          {sections.map((section, index) => {
            if (section.toLowerCase().includes('timeline') || section.toLowerCase().includes('milestone')) {
              const rows = section.split('\n').filter(row => row.trim());
              const tableRows = rows.slice(1).filter(row => row.includes('|'));
              
              if (tableRows.length > 0) {
                return (
                  <div key={index} className="my-8">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="bg-muted/50 p-4 text-left font-sans text-base font-medium tracking-wide uppercase text-muted-foreground border border-border">
                              Milestone
                            </th>
                            <th className="bg-muted/50 p-4 text-left font-sans text-base font-medium tracking-wide uppercase text-muted-foreground border border-border">
                              Timeline
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {tableRows.map((row, i) => {
                            const [milestone, timeline] = row
                              .split('|')
                              .map(cell => cell.trim())
                              .filter(Boolean);
                            return (
                              <tr key={i} className="even:bg-muted/5">
                                <td className="p-4 border border-border">
                                  <span className="font-sans text-sm font-medium text-primary">
                                    {formatText(milestone)}
                                  </span>
                                </td>
                                <td className="p-4 border border-border">
                                  <span className="font-sans text-sm font-medium text-primary">
                                    {formatText(timeline)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }
            }

            const lines = section.split('\n');
            const firstLine = lines[0];
            const remainingLines = lines.slice(1);

            const isHeading = firstLine.length < 100 && 
              (firstLine.endsWith(':') || 
               firstLine.toUpperCase() === firstLine ||
               firstLine.startsWith('#') ||
               /^[A-Z][a-z]+(.*?):?$/.test(firstLine));

            // Add payment terms after the cost section
            if (firstLine.toLowerCase().includes('cost') || firstLine.toLowerCase().includes('budget')) {
              return (
                <div key={index} className="mb-6">
                  {isHeading ? (
                    <h3 className="text-xl font-bold mb-4">{formatText(firstLine)}</h3>
                  ) : (
                    <p className="mb-4 leading-relaxed">{formatText(firstLine)}</p>
                  )}
                  {remainingLines.map((line, i) => (
                    <p key={i} className="mb-4 leading-relaxed">
                      {formatText(line)}
                    </p>
                  ))}
                  <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
                    <h4 className="text-sm font-bold mb-2">Payment Terms</h4>
                    <p className="text-sm text-muted-foreground">
                      50% of the project cost is due upon signing the proposal. The remaining 50% is due upon project completion.
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className="mb-6">
                {isHeading ? (
                  <h3 className="text-xl font-bold mb-4">{formatText(firstLine)}</h3>
                ) : (
                  <p className="mb-4 leading-relaxed">{formatText(firstLine)}</p>
                )}
                {remainingLines.map((line, i) => (
                  <p key={i} className="mb-4 leading-relaxed">
                    {formatText(line)}
                  </p>
                ))}
              </div>
            );
          })}

          <div className="mt-8 space-y-4">
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-bold mb-2">Proposal Validity</h4>
              <p className="text-sm text-muted-foreground">
                This proposal is valid until {formatDate(validUntil)} ({formatDate(today)} + 30 days).
              </p>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-bold mb-2">Disclaimer</h4>
              <p className="text-sm text-muted-foreground">
                The pricing outlined in this proposal is based on the scope of work as described. Any additional requests or changes
                to the project scope may result in an adjustment to the final cost. We are committed to transparent communication and will
                promptly discuss any changes to ensure alignment with your needs and budget.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}